import { FLAVOURS, PROTEINS, type Flavour, type Menu, type MenuItem, type Protein } from '../menu.js';
import type { TillChange } from './settings.js';

/*
 * The till's layout as data (CLAUDE.md §3, Saif 19 Oct 2026: "I don't want anything hard code"). Layout A from
 * docs/08 is only the starting layout. Saif edits the rest in the admin: the columns (how many, their order and
 * widths), the sections in each column (title, buttons across, which buttons in what order), short till names,
 * wide buttons, which items are on the till and which on the app, the flavour names, the button height, the
 * ticket width and the building strip's buttons.
 *
 * Docket's rules are guard rails, checked here so the admin and the server refuse the same things:
 *  - buttons on the order path are never under 54px tall (and never too narrow to read);
 *  - a layout carries no colours: every button takes its colour from what it is (flavour, main, side, drink...),
 *    so colour keeps its Docket meaning;
 *  - everything fits on one 11-inch iPad screen (1210 × 834) with the offline bar showing, with no page scroll.
 */

/** One button (or one "fill" that becomes several) in a section. */
export type TillButtonRef =
  | { t: 'item'; id: string }
  | { t: 'flavour'; flavour: Flavour; protein: Protein }
  | { t: 'change'; id: string }
  /** Every till item in this menu category that isn't placed by name somewhere else, in menu order. New items appear here. */
  | { t: 'category'; category: string }
  /** Every sauce and salad button that isn't placed by name somewhere else, in the order of the Till buttons settings. */
  | { t: 'changes' };

export type TillSectionHeader = 'title' | 'proteins' | 'none';

export interface TillSection {
  id: string;
  /** Shown above the buttons when the header is "title" (in capitals on the till). */
  title: string;
  /** "proteins" puts a CHICKEN / PANEER chip above each column of buttons, from the flavours in it. */
  header: TillSectionHeader;
  /** Buttons per row, 1 to 4. */
  across: number;
  buttons: TillButtonRef[];
}

export interface TillColumn {
  id: string;
  /** Its share of the width, in parts (1 to 12). */
  width: number;
  sections: TillSection[];
}

/** Per menu item. Anything missing means the default: the menu name in capitals, a normal button, on the till and on the app. */
export interface TillItemSettings {
  /** A short name for the till button (and the till's ticket), e.g. "T&C". */
  name?: string;
  /** A big button: two columns wide, or two rows tall in a section with one button across. */
  wide?: boolean;
  /** Off: the item isn't on the till (an app-only item). */
  onTill?: boolean;
  /** Off: the item isn't in the app or on the website (a till-only item). */
  onApp?: boolean;
}

export interface TillStripSettings {
  /** The ⌫ UNDO FLAVOUR button in the building strip. */
  undoFlavour: boolean;
  /** The + SAME AGAIN button. */
  sameAgain: boolean;
  /** What the strip says before a main is tapped. */
  hint: string;
}

export interface TillLayout {
  columns: TillColumn[];
  items: Record<string, TillItemSettings>;
  /** Short flavour names on the buttons and in the strip; missing means the flavour's name in capitals. */
  flavourNames: Partial<Record<Flavour, string>>;
  /** The smallest height of a button on the order path, in px (54 or more). Buttons grow to fill the column. */
  buttonHeight: number;
  /** The ticket on the right, in px. */
  ticketWidth: number;
  strip: TillStripSettings;
}

/* ---------- the starting layout (layout A) ---------- */

const flavourGrid = (): TillButtonRef[] => FLAVOURS.flatMap((f) => PROTEINS.map((p): TillButtonRef => ({ t: 'flavour', flavour: f, protein: p })));

/** Layout A from docs/08 and design/docket-pos: MAIN | FLAVOURS (chicken, paneer) | SAUCE & SALAD | SIDES, DESSERTS, DRINKS, at the prototype's widths (1 : 1.75 : 0.8 : 1.7, near enough in whole parts). */
export const DEFAULT_TILL_LAYOUT: TillLayout = {
  columns: [
    { id: 'mains', width: 6, sections: [{ id: 'mains', title: 'MAIN', header: 'title', across: 1, buttons: [{ t: 'category', category: 'bowls' }, { t: 'category', category: 'just' }] }] },
    { id: 'flavours', width: 10, sections: [{ id: 'flavours', title: 'FLAVOURS', header: 'proteins', across: 2, buttons: flavourGrid() }] },
    { id: 'changes', width: 5, sections: [{ id: 'changes', title: 'SAUCE & SALAD', header: 'title', across: 1, buttons: [{ t: 'changes' }] }] },
    {
      id: 'extras', width: 10, sections: [
        { id: 'sides', title: 'SIDES', header: 'title', across: 2, buttons: [{ t: 'category', category: 'sides' }] },
        { id: 'desserts', title: 'DESSERTS', header: 'title', across: 2, buttons: [{ t: 'category', category: 'desserts' }] },
        { id: 'drinks', title: 'DRINKS', header: 'title', across: 2, buttons: [{ t: 'category', category: 'drinks' }] },
      ],
    },
  ],
  items: {},
  flavourNames: {},
  buttonHeight: 54,
  ticketWidth: 372,
  strip: { undoFlavour: true, sameAgain: true, hint: 'TAP A MAIN · THEN ITS FLAVOURS · 3RD FLAVOUR MAKES IT LARGE' },
};

/* ---------- limits ---------- */

export const TILL_LAYOUT_LIMITS = {
  columns: 6,
  sectionsPerColumn: 8,
  buttonsPerSection: 40,
  across: 4,
  width: 12,
  minButtonHeight: 54,
  maxButtonHeight: 120,
  minTicketWidth: 320,
  maxTicketWidth: 440,
  /** Narrower than this and a two-word name can't be read at arm's length. */
  minButtonWidth: 72,
  titleLength: 24,
  nameLength: 18,
  flavourNameLength: 12,
  hintLength: 80,
} as const;

/** The iPad screen the layout must fit, and the fixed parts of the till around the buttons (docket.css). */
export const TILL_SCREEN = {
  width: 1210,
  height: 834,
  header: 60,
  /** The dashed offline bar, counted so the layout still fits when the internet drops. */
  offbar: 28,
  strip: 66,
  /** Padding around the left side, top and bottom (and left and right). */
  pad: 12,
  /** Between the strip and the columns, and between columns. */
  gap: 10,
  /** Between the parts of a column: titles, header chips, rows, sections. */
  inner: 8,
  title: 12,
  proteinHeader: 26,
  /** The ticket's own left border. */
  ticketBorder: 3,
} as const;

export class TillLayoutError extends Error {
  override name = 'TillLayoutError';
}

/* ---------- validation ---------- */

const ID = /^[a-z0-9][a-z0-9-]{0,23}$/;
const isRec = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const text = (v: unknown, max: number, what: string): string => {
  if (typeof v !== 'string') throw new TillLayoutError(`${what} must be text`);
  const s = v.replace(/\s+/g, ' ').trim();
  if (s.length > max) throw new TillLayoutError(`${what} is longer than ${max} characters`);
  return s;
};
const whole = (v: unknown, min: number, max: number, what: string): number => {
  if (typeof v !== 'number' || !Number.isInteger(v) || v < min || v > max) throw new TillLayoutError(`${what} must be a whole number from ${min} to ${max}`);
  return v;
};

function ref(raw: unknown, where: string): TillButtonRef {
  if (!isRec(raw)) throw new TillLayoutError(`A button in ${where} is not a button`);
  switch (raw.t) {
    case 'item':
      if (typeof raw.id !== 'string' || !raw.id || raw.id.length > 60) throw new TillLayoutError(`A menu item in ${where} has no id`);
      return { t: 'item', id: raw.id };
    case 'flavour':
      if (!FLAVOURS.includes(raw.flavour as Flavour) || !PROTEINS.includes(raw.protein as Protein)) throw new TillLayoutError(`A flavour button in ${where} names an unknown flavour or protein`);
      return { t: 'flavour', flavour: raw.flavour as Flavour, protein: raw.protein as Protein };
    case 'change':
      if (typeof raw.id !== 'string' || !raw.id || raw.id.length > 24) throw new TillLayoutError(`A sauce and salad button in ${where} has no id`);
      return { t: 'change', id: raw.id };
    case 'category':
      if (typeof raw.category !== 'string' || !/^[a-z]{2,20}$/.test(raw.category)) throw new TillLayoutError(`A category in ${where} has no name`);
      return { t: 'category', category: raw.category };
    case 'changes':
      return { t: 'changes' };
    default:
      throw new TillLayoutError(`A button in ${where} is of an unknown kind`);
  }
}

const refKey = (r: TillButtonRef): string => (r.t === 'item' ? `item:${r.id}` : r.t === 'flavour' ? `flavour:${r.flavour}:${r.protein}` : r.t === 'change' ? `change:${r.id}` : r.t === 'category' ? `category:${r.category}` : 'changes');

/**
 * Check a layout from the admin (or the database) and return it cleaned: whitespace trimmed, unknown fields dropped
 * (so nothing like a colour can ride along), every number in range. Throws TillLayoutError with a sentence for Saif.
 */
export function validateTillLayout(raw: unknown): TillLayout {
  const L = TILL_LAYOUT_LIMITS;
  if (!isRec(raw)) throw new TillLayoutError('The layout is missing');
  if (!Array.isArray(raw.columns) || raw.columns.length < 1) throw new TillLayoutError('The till needs at least one column');
  if (raw.columns.length > L.columns) throw new TillLayoutError(`At most ${L.columns} columns fit on the till`);
  const ids = new Set<string>();
  const seen = new Map<string, string>();
  const columns = raw.columns.map((c, ci): TillColumn => {
    const cw = `column ${ci + 1}`;
    if (!isRec(c)) throw new TillLayoutError(`Column ${ci + 1} is not a column`);
    if (typeof c.id !== 'string' || !ID.test(c.id) || ids.has(`c:${c.id}`)) throw new TillLayoutError(`Column ${ci + 1} needs its own id`);
    ids.add(`c:${c.id}`);
    if (!Array.isArray(c.sections) || c.sections.length < 1) throw new TillLayoutError(`Column ${ci + 1} needs at least one section`);
    if (c.sections.length > L.sectionsPerColumn) throw new TillLayoutError(`Column ${ci + 1} has more than ${L.sectionsPerColumn} sections`);
    const sections = c.sections.map((s, si): TillSection => {
      if (!isRec(s)) throw new TillLayoutError(`Section ${si + 1} in ${cw} is not a section`);
      if (typeof s.id !== 'string' || !ID.test(s.id) || ids.has(`s:${s.id}`)) throw new TillLayoutError(`Section ${si + 1} in ${cw} needs its own id`);
      ids.add(`s:${s.id}`);
      const title = text(s.title ?? '', L.titleLength, `The title of section ${si + 1} in ${cw}`).toUpperCase();
      const where = title ? `“${title}”` : `section ${si + 1} of ${cw}`;
      const header: TillSectionHeader = s.header === 'proteins' || s.header === 'none' ? s.header : 'title';
      const across = whole(s.across ?? 1, 1, L.across, `Buttons across in ${where}`);
      if (!Array.isArray(s.buttons)) throw new TillLayoutError(`${where} has no button list`);
      if (s.buttons.length > L.buttonsPerSection) throw new TillLayoutError(`${where} has more than ${L.buttonsPerSection} buttons`);
      const buttons = s.buttons.map((b) => {
        const r = ref(b, where);
        const k = refKey(r);
        const prev = seen.get(k);
        if (prev) throw new TillLayoutError(`${describeRef(r)} is in ${prev} and ${where}: a button can be on the till once`);
        seen.set(k, where);
        return r;
      });
      return { id: s.id, title, header, across, buttons };
    });
    return { id: c.id, width: whole(c.width ?? 1, 1, L.width, `The width of column ${ci + 1}`), sections };
  });
  const items: Record<string, TillItemSettings> = {};
  if (raw.items !== undefined) {
    if (!isRec(raw.items)) throw new TillLayoutError('The item settings are not a list');
    for (const [id, v] of Object.entries(raw.items)) {
      if (!id || id.length > 60 || !isRec(v)) continue;
      const it: TillItemSettings = {};
      if (v.name !== undefined && v.name !== '') {
        const n = text(v.name, L.nameLength, `The till name of ${id}`);
        if (n) it.name = n;
      }
      if (v.wide === true) it.wide = true;
      if (v.onTill === false) it.onTill = false;
      if (v.onApp === false) it.onApp = false;
      if (it.onTill === false && it.onApp === false) throw new TillLayoutError(`${it.name ?? id} would be on neither the till nor the app. Switch it off in Menu instead.`);
      if (Object.keys(it).length) items[id] = it;
    }
  }
  const flavourNames: Partial<Record<Flavour, string>> = {};
  if (raw.flavourNames !== undefined) {
    if (!isRec(raw.flavourNames)) throw new TillLayoutError('The flavour names are not a list');
    for (const f of FLAVOURS) {
      const v = raw.flavourNames[f];
      if (v === undefined || v === '') continue;
      const n = text(v, L.flavourNameLength, `The till name of ${f}`).toUpperCase();
      if (n) flavourNames[f] = n;
    }
  }
  const st = isRec(raw.strip) ? raw.strip : {};
  const strip: TillStripSettings = {
    undoFlavour: st.undoFlavour !== false,
    sameAgain: st.sameAgain !== false,
    hint: st.hint === undefined ? DEFAULT_TILL_LAYOUT.strip.hint : text(st.hint, L.hintLength, 'The strip’s hint').toUpperCase(),
  };
  return {
    columns, items, flavourNames,
    buttonHeight: whole(raw.buttonHeight ?? DEFAULT_TILL_LAYOUT.buttonHeight, L.minButtonHeight, L.maxButtonHeight, 'The button height'),
    ticketWidth: whole(raw.ticketWidth ?? DEFAULT_TILL_LAYOUT.ticketWidth, L.minTicketWidth, L.maxTicketWidth, 'The ticket width'),
    strip,
  };
}

function describeRef(r: TillButtonRef): string {
  switch (r.t) {
    case 'item': return `The item ${r.id}`;
    case 'flavour': return `${r.flavour} ${r.protein}`;
    case 'change': return `The sauce and salad button ${r.id}`;
    case 'category': return `All ${r.category}`;
    default: return 'All sauce and salad buttons';
  }
}

/* ---------- from data to buttons ---------- */

export type TillButtonKind = 'main' | 'extra' | 'flavour' | 'change';
export interface TillButton {
  /** Unique on the screen: "item:rice", "flavour:Classic:chicken", "change:noonion". */
  key: string;
  kind: TillButtonKind;
  label: string;
  /** Grid cells it spans in its row (a wide item spans 2 when there's room; the last button fills its row). */
  span: number;
  /** Rows it spans: a wide item in a one-across section is two rows tall instead. */
  rowSpan: number;
  item?: MenuItem;
  flavour?: Flavour;
  protein?: Protein;
  change?: TillChange;
}
export interface TillResolvedSection {
  id: string;
  title: string;
  header: TillSectionHeader;
  across: number;
  buttons: TillButton[];
  rows: number;
  /** For header "proteins": the protein of each grid column (null where the column has no flavour). */
  proteins: (Protein | null)[];
}
export interface TillResolvedColumn { id: string; width: number; sections: TillResolvedSection[] }
export interface TillResolvedLayout {
  columns: TillResolvedColumn[];
  /** Items on the till that no section shows (so Saif can place them). */
  unplaced: MenuItem[];
}

export function tillItemSettings(layout: Pick<TillLayout, 'items'>, id: string): TillItemSettings {
  return layout.items[id] ?? {};
}
export const onTill = (layout: Pick<TillLayout, 'items'>, id: string): boolean => layout.items[id]?.onTill !== false;
export const onApp = (layout: Pick<TillLayout, 'items'>, id: string): boolean => layout.items[id]?.onApp !== false;
/** The name on an item's till button: its short till name, or its menu name (mains show it in capitals). */
export function tillItemName(layout: Pick<TillLayout, 'items'>, item: Pick<MenuItem, 'id' | 'name'>): string {
  return layout.items[item.id]?.name || item.name;
}
export function tillFlavourName(layout: Pick<TillLayout, 'flavourNames'>, f: Flavour): string {
  return layout.flavourNames[f] || f.toUpperCase();
}

const bySort = (a: MenuItem, b: MenuItem) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name);

/** Place the buttons of a section in rows of `across`; returns how many rows and sets each button's span. */
function placeRows(buttons: TillButton[], across: number): number {
  if (across === 1) {
    // One across: a big button is two rows tall.
    for (const b of buttons) {
      b.rowSpan = b.span > 1 ? 2 : 1;
      b.span = 1;
    }
    return buttons.reduce((a, b) => a + b.rowSpan, 0);
  }
  let rows = 0;
  let used = across; // cells used in the current row (start "full" so the first button opens a row)
  buttons.forEach((b, i) => {
    const want = Math.min(b.span, across);
    if (used + want > across) {
      rows++;
      used = 0;
    }
    b.span = want;
    used += want;
    // The last button stretches to the end of its row, so a row never ends in a gap.
    if (i === buttons.length - 1 && used < across) {
      b.span += across - used;
      used = across;
    }
  });
  return rows;
}

/**
 * Turn the layout into the buttons the till draws, from today's menu and sauce and salad list. Items off the menu or
 * off the till are skipped; a category fills with the till items not placed by name anywhere else.
 */
export function resolveTillLayout(layout: TillLayout, menu: Menu, changes: readonly TillChange[]): TillResolvedLayout {
  const placedItems = new Set<string>();
  const placedChanges = new Set<string>();
  for (const c of layout.columns) for (const s of c.sections) for (const b of s.buttons) {
    if (b.t === 'item') placedItems.add(b.id);
    if (b.t === 'change') placedChanges.add(b.id);
  }
  const shown = new Set<string>();
  const itemButton = (it: MenuItem): TillButton => ({ key: `item:${it.id}`, kind: it.hasTikkas ? 'main' : 'extra', label: tillItemName(layout, it), span: layout.items[it.id]?.wide ? 2 : 1, rowSpan: 1, item: it });
  const usable = (it: MenuItem | undefined): it is MenuItem => !!it && onTill(layout, it.id);
  const columns = layout.columns.map((c): TillResolvedColumn => ({
    id: c.id,
    width: c.width,
    sections: c.sections.map((s): TillResolvedSection => {
      const buttons: TillButton[] = [];
      for (const b of s.buttons) {
        if (b.t === 'item') {
          const it = menu.items[b.id];
          if (usable(it) && !shown.has(it.id)) {
            shown.add(it.id);
            buttons.push(itemButton(it));
          }
        } else if (b.t === 'category') {
          for (const it of Object.values(menu.items).filter((x) => x.category === b.category && !placedItems.has(x.id) && !shown.has(x.id) && usable(x)).sort(bySort)) {
            shown.add(it.id);
            buttons.push(itemButton(it));
          }
        } else if (b.t === 'flavour') {
          buttons.push({ key: `flavour:${b.flavour}:${b.protein}`, kind: 'flavour', label: tillFlavourName(layout, b.flavour), span: 1, rowSpan: 1, flavour: b.flavour, protein: b.protein });
        } else if (b.t === 'change') {
          const ch = changes.find((x) => x.id === b.id);
          if (ch) buttons.push({ key: `change:${ch.id}`, kind: 'change', label: ch.label, span: 1, rowSpan: 1, change: ch });
        } else {
          for (const ch of changes.filter((x) => !placedChanges.has(x.id))) {
            placedChanges.add(ch.id);
            buttons.push({ key: `change:${ch.id}`, kind: 'change', label: ch.label, span: 1, rowSpan: 1, change: ch });
          }
        }
      }
      const rows = placeRows(buttons, s.across);
      const proteins: (Protein | null)[] = Array.from({ length: s.across }, (_, col) => {
        let cell = 0;
        for (const b of buttons) {
          if (cell % s.across === col && b.kind === 'flavour') return b.protein ?? null;
          cell += b.span;
        }
        return null;
      });
      return { id: s.id, title: s.title, header: s.header, across: s.across, buttons, rows, proteins };
    }),
  }));
  const unplaced = Object.values(menu.items).filter((it) => onTill(layout, it.id) && !shown.has(it.id) && it.category !== 'secret').sort(bySort);
  return { columns, unplaced };
}

/* ---------- does it fit on the iPad? ---------- */

export interface TillColumnFit {
  id: string;
  /** Height the column needs with every button at the minimum height, and what the screen has. */
  needs: number;
  has: number;
  /** The column's width, and its narrowest button. */
  width: number;
  narrowest: number;
}
export interface TillFit {
  ok: boolean;
  /** One plain sentence per problem, naming the column (and section). */
  problems: string[];
  columns: TillColumnFit[];
}

const columnName = (c: TillResolvedColumn, i: number) => {
  const t = c.sections.map((s) => s.title).filter(Boolean)[0];
  return t ? `Column ${i + 1} (${t})` : `Column ${i + 1}`;
};

/** The space the left side has on an 11-inch iPad (1210 × 834) with the offline bar showing. */
export function tillAreaSize(ticketWidth: number): { width: number; height: number } {
  const S = TILL_SCREEN;
  return {
    width: S.width - ticketWidth - S.ticketBorder - 2 * S.pad,
    height: S.height - S.header - S.offbar - 2 * S.pad - S.strip - S.gap,
  };
}

/** Section height at the minimum button height. */
export function tillSectionHeight(s: Pick<TillResolvedSection, 'header' | 'title' | 'rows'>, buttonHeight: number): number {
  const S = TILL_SCREEN;
  let h = 0;
  if (s.header === 'title' && s.title) h += S.title + S.inner;
  if (s.header === 'proteins') h += S.proteinHeader + S.inner;
  if (s.rows > 0) h += s.rows * buttonHeight + (s.rows - 1) * S.inner;
  return h;
}

/** Check a resolved layout against one 11-inch screen: every column's height and every button's width. */
export function tillLayoutFit(layout: TillLayout, resolved: TillResolvedLayout): TillFit {
  const S = TILL_SCREEN;
  const L = TILL_LAYOUT_LIMITS;
  const area = tillAreaSize(layout.ticketWidth);
  const totalParts = layout.columns.reduce((a, c) => a + c.width, 0) || 1;
  const widthForColumns = area.width - S.gap * (layout.columns.length - 1);
  const problems: string[] = [];
  const columns = resolved.columns.map((c, i): TillColumnFit => {
    const parts = c.sections.map((s) => tillSectionHeight(s, layout.buttonHeight)).filter((h) => h > 0);
    const needs = parts.reduce((a, h) => a + h, 0) + S.inner * Math.max(0, parts.length - 1);
    const width = Math.floor((widthForColumns * c.width) / totalParts);
    let narrowest = Infinity;
    for (const s of c.sections) {
      if (!s.buttons.length) continue;
      const cell = (width - S.inner * (s.across - 1)) / s.across;
      narrowest = Math.min(narrowest, Math.floor(cell));
      const name = columnName(c, i);
      const inSection = s.title && !name.includes(`(${s.title})`) ? ` in ${s.title}` : '';
      if (cell < L.minButtonWidth) problems.push(`${name}: ${s.across} buttons across${inSection} are ${Math.floor(cell)}px wide, under ${L.minButtonWidth}px. Make the column wider or put fewer buttons across.`);
    }
    if (needs > area.height) problems.push(`${columnName(c, i)} is ${needs - area.height}px too tall for the iPad. Take out a row, put more buttons across, make the buttons shorter, or move a section to another column.`);
    return { id: c.id, needs, has: area.height, width, narrowest: Number.isFinite(narrowest) ? narrowest : width };
  });
  if (!resolved.columns.some((c) => c.sections.some((s) => s.buttons.length))) problems.push('The till has no buttons on it.');
  const mains = resolved.columns.some((c) => c.sections.some((s) => s.buttons.some((b) => b.kind === 'main')));
  const flavours = resolved.columns.some((c) => c.sections.some((s) => s.buttons.some((b) => b.kind === 'flavour')));
  if (mains && !flavours) problems.push('Mains are on the till but no flavours are, so a main could never be finished. Add the flavour buttons.');
  return { ok: problems.length === 0, problems, columns };
}

/** Validate, resolve and fit-check in one go (the admin's save and the server). */
export function checkTillLayout(raw: unknown, menu: Menu, changes: readonly TillChange[]): { layout: TillLayout; resolved: TillResolvedLayout; fit: TillFit } {
  const layout = validateTillLayout(raw);
  const resolved = resolveTillLayout(layout, menu, changes);
  return { layout, resolved, fit: tillLayoutFit(layout, resolved) };
}

/** A stored layout that no longer validates (or none at all) falls back to the starting layout, so the till always opens. */
export function storedTillLayout(raw: unknown): TillLayout {
  if (raw === undefined || raw === null) return DEFAULT_TILL_LAYOUT;
  try {
    return validateTillLayout(raw);
  } catch {
    return DEFAULT_TILL_LAYOUT;
  }
}
